import { z } from "zod";
import {
  balancePaymentStatusSchema,
  classKindSchema,
  creditReasonSchema,
  freeCancellationModeSchema,
  paymentRequestKindSchema,
  paymentMethodSchema,
  paymentRequestStatusSchema,
  paymentStatusSchema,
  purchaseStatusSchema,
  recurringFeeIntervalSchema,
  refundStatusSchema,
} from "../enums.ts";
import {
  filsSchema,
  isoDateSchema,
  isoDateTimeSchema,
  timestampsSchema,
  uuidSchema,
} from "./common.ts";

export const packagePurchaseSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    userId: uuidSchema,
    participantId: uuidSchema.nullable(),
    productId: uuidSchema,
    creditsTotal: z.number().int(),
    priceFils: filsSchema,
    purchasedAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
    paymentId: uuidSchema.nullable(),
    status: purchaseStatusSchema,
    /** The allowance as it was when this was bought. Null means unlimited. */
    freeCancellations: z.number().int().min(0).nullable(),
    /** The mode as it was when this was bought. Defaults so older clients keep parsing. */
    freeCancellationMode: freeCancellationModeSchema.default("any_cancel"),
    /** The membership this purchase is one month of (M2, migration 0079). Null for a pack. */
    membershipId: uuidSchema.nullable().optional(),
    /** The first instant of a membership month. The month ends at `expiresAt`. */
    periodStartsAt: isoDateTimeSchema.nullable().optional(),
    /** A month of a membership with no cap. */
    unlimited: z.boolean().optional(),
  })
  .merge(timestampsSchema);
export type PackagePurchase = z.infer<typeof packagePurchaseSchema>;

/**
 * One purchase as the member sees it. `creditsRemaining` is derived on the server from the ledger,
 * so the app never has to add up entries or guess.
 */
export const creditPurchaseSchema = packagePurchaseSchema.extend({
  businessName: z.string(),
  productName: z.string(),
  creditsRemaining: z.number().int(),
  /** Null means the credits work for any group of the package's kind. */
  groupIds: z.array(uuidSchema).nullable(),
  /**
   * The package's kind: a group package pays for group sessions only, a private one for private
   * slots only (the booking's rule, `usablePurchases`). Defaults so older clients keep parsing.
   */
  productKind: classKindSchema.default("group"),
  /**
   * How many of this purchase's free cancellations are gone, counted from the ledger rather than
   * stored, exactly like `creditsRemaining`. Null allowance means there is nothing to count against.
   */
  cancellationsUsed: z.number().int(),
  /**
   * Net sessions the business added to (or removed from) this package by hand. The member reads
   * "of N" as `creditsTotal + creditsAdded`, so a courtesy session never shows as "2 of 1 left".
   * Defaults so clients written before the field keep parsing.
   */
  creditsAdded: z.number().int().default(0),
  /** "owed" when the business recorded this period as not paid. Booking from it waits for the payment. */
  paymentStatus: balancePaymentStatusSchema.default("paid"),
  /** The day the period renews, for a balance the business set. Null for a package bought by card. */
  renewsOn: isoDateSchema.nullable().default(null),
});
export type CreditPurchase = z.infer<typeof creditPurchaseSchema>;

/** Total usable credits per business, ignoring expired and refunded purchases. */
export const creditBalanceSchema = z.object({
  businessId: uuidSchema,
  businessName: z.string(),
  creditsRemaining: z.number().int(),
});
export type CreditBalance = z.infer<typeof creditBalanceSchema>;

export const creditsResponseSchema = z.object({
  purchases: z.array(creditPurchaseSchema),
  balances: z.array(creditBalanceSchema),
});
export type CreditsResponse = z.infer<typeof creditsResponseSchema>;

export const creditLedgerEntrySchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    purchaseId: uuidSchema,
    delta: z.number().int(),
    reason: creditReasonSchema,
    bookingId: uuidSchema.nullable(),
    actorUserId: uuidSchema.nullable(),
    note: z.string().nullable(),
  })
  .merge(timestampsSchema);
export type CreditLedgerEntry = z.infer<typeof creditLedgerEntrySchema>;

/** `POST /v1/owner/businesses/:businessId/credits/adjust`, owner override, audit logged. */
export const adjustCreditsRequestSchema = z.object({
  purchaseId: uuidSchema,
  delta: z.number().int().refine((value) => value !== 0, "A zero adjustment does nothing"),
  note: z.string().min(1).max(500),
});
export type AdjustCreditsRequest = z.infer<typeof adjustCreditsRequestSchema>;

export const adjustCreditsResponseSchema = z.object({
  entry: creditLedgerEntrySchema,
  creditsRemaining: z.number().int(),
});
export type AdjustCreditsResponse = z.infer<typeof adjustCreditsResponseSchema>;

/** `POST /v1/businesses/:businessId/packages/:productId/checkout` */
export const checkoutRequestSchema = z.object({
  participantId: uuidSchema.nullable().default(null),
  /** Where the in-app browser should land after the hosted checkout finishes. */
  returnUrl: z.string().url().optional(),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

export const checkoutResponseSchema = z.object({
  paymentId: uuidSchema,
  provider: z.string(),
  providerPaymentId: z.string(),
  /** Open this in an in-app browser, then poll `GET /v1/payments/:id`. */
  checkoutUrl: z.string().url(),
  amountFils: filsSchema,
  /** Always 0: Teamna takes no fee (decision 3, 2026-09-25). Kept for older app builds; remove later. */
  platformFeeFils: filsSchema,
  currency: z.string(),
  expiresAt: isoDateTimeSchema.nullable(),
});
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

export const paymentSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    userId: uuidSchema,
    provider: z.string(),
    providerPaymentId: z.string(),
    amountFils: filsSchema,
    /** Always 0: Teamna takes no fee. Kept for older app builds; remove later. */
    platformFeeFils: filsSchema,
    /** The provider's fee on this payment (Stripe's fee plus VAT), paid by the business. */
    gatewayFeeFils: filsSchema.nullable(),
    currency: z.string(),
    status: paymentStatusSchema,
    /** Card, cash or bank transfer. Optional for older API builds, which only took cards. */
    method: paymentMethodSchema.default("card"),
    /** What the owner typed with a cash or transfer payment. */
    note: z.string().nullable().default(null),
    paidAt: isoDateTimeSchema.nullable(),
  })
  .merge(timestampsSchema);
export type Payment = z.infer<typeof paymentSchema>;

/**
 * A proof of payment (migration 0084): one photo or PDF of a receipt or a bank transfer on a cash or
 * transfer payment. Never public: `url` is a signed address that expires.
 */
export const PAYMENT_PROOF_LIMITS = { fileBytes: 10 * 1024 * 1024, fileName: 120 } as const;
export const paymentProofTypes = ["image/jpeg", "image/png", "application/pdf"] as const;
export const paymentProofTypeSchema = z.enum(paymentProofTypes);
export type PaymentProofType = z.infer<typeof paymentProofTypeSchema>;

export const paymentProofSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  contentType: paymentProofTypeSchema,
  byteSize: z.number().int(),
  /** A signed address that expires. Null in the answer to an upload: the file has no payment yet. */
  url: z.string().nullable(),
});
export type PaymentProof = z.infer<typeof paymentProofSchema>;

/** `POST /business/businesses/:id/payment-proofs`: one file, before the payment that uses it. */
export const uploadPaymentProofRequestSchema = z.object({
  /** A phone can give a long name: the server keeps the first `PAYMENT_PROOF_LIMITS.fileName` characters. */
  fileName: z.string().trim().min(1).max(1000),
  contentType: paymentProofTypeSchema,
  /** The file as base64, no data URL prefix. */
  data: z.string().min(1).max(Math.ceil((PAYMENT_PROOF_LIMITS.fileBytes * 4) / 3) + 4),
});
export type UploadPaymentProofRequest = z.infer<typeof uploadPaymentProofRequestSchema>;

/** `PUT /owner/businesses/:id/payments/:paymentId/proof`: an uploaded file becomes the proof, in place of the old one. */
export const setPaymentProofRequestSchema = z.object({ proofId: uuidSchema });
export type SetPaymentProofRequest = z.infer<typeof setPaymentProofRequestSchema>;

export const paymentsListResponseSchema = z.object({
  items: z.array(
    paymentSchema.extend({
      payerName: z.string().nullable(),
      /** What the money paid for: the package name, "Recurring fee", or null when unknown. */
      what: z.string().nullable().default(null),
      /** The sum of succeeded refunds on this payment. */
      refundedFils: filsSchema.default(0),
      /** Who recorded a cash or transfer payment. Null for card. */
      recordedByName: z.string().nullable().default(null),
      /** The proof of a cash or transfer payment (migration 0084), or null. */
      proof: paymentProofSchema.nullable().default(null),
    }),
  ),
  nextCursor: z.string().nullable(),
});
export type PaymentsListResponse = z.infer<typeof paymentsListResponseSchema>;

export const refundRequestSchema = z.object({
  /** Omit to refund the full remaining amount. */
  amountFils: filsSchema.min(1).optional(),
  reason: z.string().max(500).optional(),
});
export type RefundRequest = z.infer<typeof refundRequestSchema>;

export const refundSchema = z
  .object({
    id: uuidSchema,
    paymentId: uuidSchema,
    amountFils: filsSchema,
    providerRefundId: z.string().nullable(),
    status: refundStatusSchema,
    initiatedBy: uuidSchema.nullable(),
  })
  .merge(timestampsSchema);
export type Refund = z.infer<typeof refundSchema>;

export const recurringFeeSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    enrollmentId: uuidSchema,
    amountFils: filsSchema,
    interval: recurringFeeIntervalSchema,
    nextDueOn: isoDateSchema,
    active: z.boolean(),
  })
  .merge(timestampsSchema);
export type RecurringFee = z.infer<typeof recurringFeeSchema>;

export const createRecurringFeeRequestSchema = z.object({
  enrollmentId: uuidSchema,
  amountFils: filsSchema.min(0),
  interval: recurringFeeIntervalSchema.default("monthly"),
  nextDueOn: isoDateSchema,
});
export type CreateRecurringFeeRequest = z.infer<typeof createRecurringFeeRequestSchema>;

export const updateRecurringFeeRequestSchema = z
  .object({
    amountFils: filsSchema.min(0),
    interval: recurringFeeIntervalSchema,
    nextDueOn: isoDateSchema,
    active: z.boolean(),
  })
  .partial();
export type UpdateRecurringFeeRequest = z.infer<typeof updateRecurringFeeRequestSchema>;

export const paymentRequestSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    businessName: z.string().optional(),
    userId: uuidSchema,
    kind: paymentRequestKindSchema,
    referenceId: uuidSchema.nullable(),
    /**
     * The card title the member reads, for example "Monthly fee for Zayd Khan". Built by the API
     * from whatever raised the request, so the app never has to turn a reference id into copy.
     * Null only when nothing more specific than the kind is known.
     */
    description: z.string().nullable(),
    amountFils: filsSchema,
    /** The business's currency, so the app never assumes AED. */
    currency: z.string(),
    dueOn: isoDateSchema,
    status: paymentRequestStatusSchema,
    paymentId: uuidSchema.nullable(),
  })
  .merge(timestampsSchema);
export type PaymentRequest = z.infer<typeof paymentRequestSchema>;

export const paymentRequestsResponseSchema = z.object({ items: z.array(paymentRequestSchema) });
export type PaymentRequestsResponse = z.infer<typeof paymentRequestsResponseSchema>;
